import { ReactElement } from "react";
import { Box, Text, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showSuccessNotification } from "@utils/notifications";
import ErrorMessage from "@common/components/text/ErrorMessage";
import IconGlobe from "@common/components/icons/IconGlobe";
import IconProductAnalytics from "@common/components/icons/IconProductAnalytics";
import { formatPrice, formatQuantity } from "@common/utils/string";
import { NUMBER_FORMAT_PRICE_OPTIONS } from "@common/utils/string/constants";
import useChangePlanWithInstanceRestart from "@hooks/useChangePlanWithInstanceRestart";
import AddOnToggleLink from "@components/ui/plan/AddOnToggleLink";
import { AddOnConfirmModal } from "@addons/components/AddOnConfirmModal";
import ProductAnalyticsUsageGraph from "./UsageGraph";
import { TrackCard } from "@addons/components/TrackCard";
import { useAddOnContext } from "../hooks";
import { IAddOnSlotProps, IMeteredAddOnDescriptor } from "../types";
import { hasStorage } from "../utils";

export function ManageCard({
  addOn: productAnalytics,
}: IAddOnSlotProps<IMeteredAddOnDescriptor>): ReactElement {
  const theme = useMantineTheme();

  const { purchase, subscribed, canChangeSubscription, title } =
    productAnalytics;
  const storageRent = useAddOnContext("dwh-rent");

  // enabling Track bundles Storage in when there is no warehouse yet, so the
  // copy has to name both
  const subject = hasStorage(productAnalytics) ? title : `${title} and Storage`;
  // prepaid storage bills rows up front, so there is no per-row rate to quote
  const isPrepaidStorage = !!productAnalytics.subscribedByType["dwh"];

  const includedEvents = productAnalytics.getIncludedUnits();
  const ingestPricePer1M = 1_000_000 * productAnalytics.getPricePerUnit();
  const storagePricePer1M = 1_000_000 * storageRent.getPricePerUnit();

  const formatAsNumber = (price: number) =>
    formatPrice(price, NUMBER_FORMAT_PRICE_OPTIONS);

  const storagePricingClause = isPrepaidStorage
    ? ""
    : `, ${formatAsNumber(storagePricePer1M)}/mo for storing`;
  const storageChargeClause = isPrepaidStorage
    ? ""
    : `, and ${formatPrice(storagePricePer1M)}/month for every additional 1M stored events`;

  const pricingDescription = `Price per 1M afterwards: ${formatAsNumber(ingestPricePer1M)} for ingesting${storagePricingClause}.`;
  const chargeDescription = `Afterwards, you'll be charged ${formatPrice(ingestPricePer1M)} for every additional 1M ingested events${storageChargeClause}. You can cancel and disable Track anytime.`;

  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [removeOpened, { open: openRemove, close: closeRemove }] =
    useDisclosure(false);

  const {
    isPending: changingPlan,
    isUpdatingSubscription,
    error: changePlanError,
    mutate: changePlan,
  } = useChangePlanWithInstanceRestart(purchase);

  const notifyTrackChange = (message: string) =>
    showSuccessNotification({ icon: <IconGlobe />, message });

  const enableTrack = () =>
    changePlan(productAnalytics.getEnableAddOnPayload(), {
      onSuccess: () => notifyTrackChange(`${subject} enabled.`),
    });

  const disableTrack = () =>
    changePlan(productAnalytics.getDisableAddOnPayload(), {
      onSuccess: () => notifyTrackChange("Metabase Track disabled."),
    });

  return (
    <>
      <TrackCard
        addOn={productAnalytics}
        disabled={!canChangeSubscription}
        action={
          <AddOnToggleLink
            text={subscribed ? "Disable" : "Enable"}
            loading={isUpdatingSubscription}
            disabled={changingPlan}
            onClick={subscribed ? openRemove : openAdd}
          />
        }
        pricingLine={
          !subscribed &&
          `${formatQuantity(includedEvents)} events included for free. ${pricingDescription}`
        }
        usage={
          (subscribed || changePlanError) && (
            <>
              <ProductAnalyticsUsageGraph addOn={productAnalytics} />
              <ErrorMessage mt={theme.spacing.xs} error={changePlanError} />
            </>
          )
        }
      />

      <AddOnConfirmModal
        testId="AddProductAnalytics-modal"
        opened={addOpened}
        title={`Add ${subject}?`}
        icon={<IconProductAnalytics />}
        confirmLabel={`Add ${subject}`}
        onClose={(save) => {
          if (save) {
            enableTrack();
          }

          closeAdd();
        }}
      >
        <Box>
          <Text c="gray.7" fz="sm" ta="center">
            {formatQuantity(includedEvents)} events included for free
          </Text>

          <Text c="gray.4" fz="sm" mt="xs" ta="center">
            {chargeDescription}
          </Text>
        </Box>
      </AddOnConfirmModal>

      <AddOnConfirmModal
        testId="RemoveProductAnalytics-modal"
        opened={removeOpened}
        title="Disable Metabase Track?"
        confirmLabel="Disable Metabase Track"
        cancelLabel="Keep Metabase Track"
        danger
        onClose={(save) => {
          if (save) {
            disableTrack();
          }

          closeRemove();
        }}
      >
        <Text c="gray.4" fz="sm">
          Disabling Track stops new events from being ingested. Any events
          already ingested are still available until you also disable Storage.
        </Text>
      </AddOnConfirmModal>
    </>
  );
}
